import {
	useEffect,
	useMemo,
	useRef,
	useState,
	type ChangeEvent,
	type FormEvent,
} from 'react'

import { Field } from '#app/components/form/field.tsx'
import { SelectField } from '#app/components/form/select-field.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { ResponsiveOverlay } from '#app/components/ui/responsive-overlay.tsx'
import { PRODUCT_OVERLAY_COPY } from '#app/routes/mocra+/_form+/components/products/constants.ts'
import {
	createProductDraft,
	isProductCategoryValue,
	isProductProfessionalUseValue,
	isProductStatusValue,
	sanitizeProductDraft,
	type ProductDraft,
} from '#app/routes/mocra+/_form+/components/products/types.ts'
import {
	PRODUCT_CATEGORY_OPTIONS,
	PRODUCT_STATUS_OPTIONS,
	PROFESSIONAL_USE_OPTIONS,
} from '#app/routes/mocra+/_form+/intake-options.ts'

type Mode = 'add' | 'edit'

type ProductOverlayProps = {
	readonly mode: Mode
	readonly open: boolean
	readonly onOpenChange: (open: boolean) => void
	readonly initialValue?: ProductDraft
	readonly onSubmit: (draft: ProductDraft) => void
	readonly title?: string
	readonly description?: string
	readonly submitLabel?: string
	readonly cancelLabel?: string
	readonly onCancel?: () => void
	readonly onClosed?: () => void
}

type DraftErrors = Partial<Record<keyof ProductDraft, string>>

export function ProductOverlay({
	mode,
	open,
	onOpenChange: setOpen,
	initialValue,
	onSubmit,
	title,
	description,
	submitLabel,
	cancelLabel = 'Cancel',
	onCancel,
	onClosed,
}: ProductOverlayProps) {
	const [draft, setDraft] = useState<ProductDraft>(
		initialValue ? { ...initialValue } : createProductDraft(),
	)
	const [errors, setErrors] = useState<DraftErrors>({})
	const wasOpenRef = useRef(open)

	useEffect(() => {
		if (open) {
			setDraft(initialValue ? { ...initialValue } : createProductDraft())
			setErrors({})
		}
	}, [open, initialValue])

	useEffect(() => {
		if (!open && wasOpenRef.current && typeof onClosed === 'function') {
			onClosed()
		}
		wasOpenRef.current = open
	}, [open, onClosed])

	const showCategoryOther = draft.category === 'other'

	const descriptionCopy = description ?? PRODUCT_OVERLAY_COPY.description
	const titleCopy =
		title ??
		(mode === 'add'
			? PRODUCT_OVERLAY_COPY.title.add
			: PRODUCT_OVERLAY_COPY.title.edit)
	const submitCopy =
		submitLabel ??
		(mode === 'add'
			? PRODUCT_OVERLAY_COPY.submit.add
			: PRODUCT_OVERLAY_COPY.submit.edit)

	const categoryOptions = useMemo(() => PRODUCT_CATEGORY_OPTIONS, [])
	const professionalUseOptions = useMemo(() => PROFESSIONAL_USE_OPTIONS, [])
	const statusOptions = useMemo(() => PRODUCT_STATUS_OPTIONS, [])

	const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault()
		event.stopPropagation()
		const sanitized = sanitizeProductDraft(draft)
		const validationErrors: DraftErrors = {}
		if (!sanitized.name) {
			validationErrors.name = 'Enter the product name.'
		}
		if (!isProductCategoryValue(sanitized.category)) {
			validationErrors.category = 'Select a valid category.'
		}
		if (sanitized.category === 'other' && !sanitized.categoryOther) {
			validationErrors.categoryOther = 'Describe the product category.'
		}
		if (!isProductProfessionalUseValue(sanitized.professionalUse)) {
			validationErrors.professionalUse = 'Select the professional use status.'
		}
		if (!isProductStatusValue(sanitized.status)) {
			validationErrors.status = 'Select the regulatory status.'
		}

		if (Object.keys(validationErrors).length > 0) {
			setErrors(validationErrors)
			return
		}

		onSubmit(sanitized)
		setOpen(false)
	}

	const handleCancel = () => {
		setOpen(false)
		onCancel?.()
	}

	return (
		<ResponsiveOverlay
			open={open}
			onOpenChange={setOpen}
			title={titleCopy}
			description={descriptionCopy}
			className="sm:max-w-xl"
			footer={
				<div className="flex justify-end gap-3">
					<Button type="button" variant="outline" onClick={handleCancel}>
						{cancelLabel}
					</Button>
					<Button type="submit" form="product-overlay-form">
						{submitCopy}
					</Button>
				</div>
			}
		>
			<form id="product-overlay-form" onSubmit={handleSubmit}>
				<Field
					labelProps={{ children: PRODUCT_OVERLAY_COPY.fields.nameLabel }}
					inputProps={{
						value: draft.name,
						onChange: (event: ChangeEvent<HTMLInputElement>) =>
							setDraft((prev) => ({
								...prev,
								name: event.target.value,
							})),
						type: 'text',
					}}
					errors={errors.name ? [errors.name] : undefined}
				/>
				<Field
					labelProps={{
						children: PRODUCT_OVERLAY_COPY.fields.skuLabel,
					}}
					inputProps={{
						value: draft.sku,
						onChange: (event: ChangeEvent<HTMLInputElement>) =>
							setDraft((prev) => ({
								...prev,
								sku: event.target.value,
							})),
						type: 'text',
					}}
					descriptionProps={{
						children: PRODUCT_OVERLAY_COPY.fields.skuDescription,
					}}
				/>
				<SelectField
					labelProps={{
						children: PRODUCT_OVERLAY_COPY.fields.categoryLabel,
					}}
					selectProps={{
						value: draft.category,
						onChange: (event: ChangeEvent<HTMLSelectElement>) =>
							setDraft((prev) => ({
								...prev,
								category: event.target.value as ProductDraft['category'],
							})),
						name: 'category',
					}}
					options={categoryOptions}
					errors={errors.category ? [errors.category] : undefined}
				/>
				{showCategoryOther ? (
					<Field
						labelProps={{
							children: PRODUCT_OVERLAY_COPY.fields.categoryOtherLabel,
						}}
						inputProps={{
							value: draft.categoryOther,
							onChange: (event: ChangeEvent<HTMLInputElement>) =>
								setDraft((prev) => ({
									...prev,
									categoryOther: event.target.value,
								})),
							type: 'text',
						}}
						errors={errors.categoryOther ? [errors.categoryOther] : undefined}
					/>
				) : null}
				<SelectField
					labelProps={{
						children: PRODUCT_OVERLAY_COPY.fields.professionalUseLabel,
					}}
					selectProps={{
						value: draft.professionalUse,
						onChange: (event: ChangeEvent<HTMLSelectElement>) =>
							setDraft((prev) => ({
								...prev,
								professionalUse: event.target
									.value as ProductDraft['professionalUse'],
							})),
						name: 'professionalUse',
					}}
					options={professionalUseOptions}
					errors={errors.professionalUse ? [errors.professionalUse] : undefined}
				/>
				<SelectField
					labelProps={{
						children: PRODUCT_OVERLAY_COPY.fields.statusLabel,
					}}
					selectProps={{
						value: draft.status,
						onChange: (event: ChangeEvent<HTMLSelectElement>) =>
							setDraft((prev) => ({
								...prev,
								status: event.target.value as ProductDraft['status'],
							})),
						name: 'status',
					}}
					options={statusOptions}
					errors={errors.status ? [errors.status] : undefined}
				/>
			</form>
		</ResponsiveOverlay>
	)
}
